import type { HostRequest, HostResponse, HostTransport } from "@heydaytime/truman-web";
import { parseNetworkEvent, type HeyDexNetworkCommand } from "@heyday/news-api/heydex";
import { BoxError } from "@heydex/shared/box";
import { onAppCommand, postToApp } from "../appMode";
/** An app without the additive handshake must update; native requests never fall back to a Worker. */
export async function appNetworkTransport(): Promise<{ transport: HostTransport; close(): void }> {
  const page = crypto.randomUUID();
  let next = 0;
  let closed = false;
  const pending = new Map<
    number,
    {
      receive(command: HeyDexNetworkCommand): void;
      reject(error: Error): void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();
  let accept: () => void = () => {};
  let refuse: (error: Error) => void = () => {};
  const ready = new Promise<void>((resolve, reject) => {
    accept = resolve;
    refuse = reject;
  });
  const timeout = setTimeout(
    () =>
      refuse(
        new BoxError(
          "unavailable",
          "Update HeyDay News and reopen HeyDex to enable this device's direct networking.",
        ),
      ),
    1500,
  );
  const unsubscribe = onAppCommand((command) => {
    if (command.type !== "networkReady" && command.type !== "networkReply") return;
    if (command.page !== page) return;
    if (command.type === "networkReady") {
      clearTimeout(timeout);
      accept();
      return;
    }
    const call = pending.get(command.id);
    if (!call) return;
    pending.delete(command.id);
    clearTimeout(call.timer);
    call.receive(command);
  });
  const close = () => {
    if (closed) return;
    closed = true;
    unsubscribe();
    clearTimeout(timeout);
    const error = new BoxError("lost", "The HeyDex page's native network closed");
    for (const call of pending.values()) {
      clearTimeout(call.timer);
      call.reject(error);
    }
    pending.clear();
    postToApp({ type: "networkClose", page });
  };
  try {
    postToApp({ type: "networkReady", page });
    await ready;
  } catch (error) {
    close();
    throw error;
  }
  const invoke = (
    operation:
      | { operation: "resolve"; host: string }
      | { operation: "request"; request: HostRequest },
  ): Promise<HostResponse | { address: string; family: 4 | 6 }[]> => {
    if (closed) throw new BoxError("lost", "The HeyDex page's native network closed");
    const id = next++;
    const event = parseNetworkEvent({ type: "network", page, id, ...operation });
    if (!event) throw new BoxError("failed", "The box's native network request is invalid");
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => {
          pending.delete(id);
          reject(new Error("The device network did not answer before its deadline"));
        },
        operation.operation === "resolve" ? 12000 : operation.request.timeoutMs + 2000,
      );
      pending.set(id, {
        timer,
        reject,
        receive(command) {
          if (command.type !== "networkReply") return;
          if (!command.ok) {
            reject(new Error(command.message));
            return;
          }
          if (command.operation !== operation.operation) {
            reject(new Error("The device answered a different network operation"));
            return;
          }
          resolve(command.operation === "resolve" ? command.addresses : command.response);
        },
      });
      try {
        postToApp(event);
      } catch (error) {
        pending.delete(id);
        clearTimeout(timer);
        reject(error);
      }
    });
  };
  const transport: HostTransport = {
    resolve: async (host) => {
      const value = await invoke({ operation: "resolve", host });
      if (!Array.isArray(value)) throw new Error("The device returned no DNS addresses");
      return value;
    },
    request: async (request) => {
      const value = await invoke({ operation: "request", request });
      if (Array.isArray(value)) throw new Error("The device returned no HTTP response");
      return value;
    },
  };
  return { transport, close };
}
